import { useState } from "react";
import { DialogTrigger, Heading, Text } from "react-aria-components";
import { type FieldValues, type Path, type UseFormSetError, useForm } from "react-hook-form";
import { useTestAppError, useTestTimeoutError } from "@/api/admin/test-errors";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Select } from "@/components/base/select/select";
import { FormField, FormItem } from "@/components/ui/form";

type ErrorKind = "validation" | "notFound" | "internalServer" | "auth" | "forbidden" | "old" | "logged" | "timeout";

type FormValues = {
  kind: ErrorKind;
};
function applyFormErrors<TFieldValues extends FieldValues>(
  formErrors: Record<string, string>,
  setError: UseFormSetError<TFieldValues>,
) {
  for (const [field, message] of Object.entries(formErrors)) {
    setError(field as `root.${string}` | "root" | Path<TFieldValues>, {
      type: "server",
      message,
    });
  }
}
type TestErrorsModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export const TestErrorsModal = ({ open: controlledOpen, onOpenChange, hideTrigger }: TestErrorsModalProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const form = useForm<FormValues>({
    defaultValues: { kind: "validation" },
  });

  const errorKind = form.watch("kind");
  const { mutateAsync: testAppError, isPending: isAppErrorPending } = useTestAppError(
    errorKind === "timeout" ? "validation" : errorKind,
    (err) => applyFormErrors(err.formErrors, form.setError),
  );
  const { mutateAsync: testTimeout, isPending: isTimeoutPending } = useTestTimeoutError();

  const onSubmit = async () => {
    try {
      if (errorKind === "timeout") {
        await testTimeout();
      } else {
        await testAppError();
      }
    } catch {
      // Error will still be handled by global mutation error handler
    }
  };

  const isPending = isAppErrorPending || isTimeoutPending;

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {hideTrigger ? (
        <button type="button" className="hidden">
          Test Errors
        </button>
      ) : (
        <Button color="secondary">Test Errors</Button>
      )}
      <ModalOverlay isDismissable>
        <Modal className="dark-mode">
          <Dialog>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="relative rounded-2xl bg-primary shadow-none ">
                <CloseButton
                  onClick={() => setOpen(false)}
                  theme="light"
                  size="lg"
                  className="absolute top-3 right-3"
                />
                <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                  <div className="z-10 flex flex-col gap-0.5">
                    <Heading slot="title" className="text-md font-semibold text-primary">
                      Test Error Handling
                    </Heading>
                    <p className="text-tertiary text-sm">Test different error types to see how they're handled</p>
                  </div>
                </div>
                <div className="h-5 w-full" />
                <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                  <FormField
                    control={form.control}
                    name="kind"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Select
                          label="Error Type"
                          placeholder="Select error type"
                          isRequired
                          size="md"
                          items={[
                            { id: "validation", label: "Validation Error (400)" },
                            { id: "formValidation", label: "Form Validation Error (400)" },
                            { id: "notFound", label: "Not Found Error (404)" },
                            { id: "auth", label: "Auth Error (401)" },
                            { id: "forbidden", label: "Forbidden Error (403)" },
                            { id: "internalServer", label: "Internal Server Error (500)" },
                            { id: "old", label: "Old Style HTTPException (500)" },
                            { id: "logged", label: "Logged Error, request succeeds (200)" },
                            { id: "timeout", label: "Timeout Error (60s)" },
                          ]}
                          selectedKey={field.value}
                          onSelectionChange={(v) => field.onChange(v)}
                          popoverClassName="dark-mode"
                        >
                          {(item) => (
                            <Select.Item key={item.id} id={item.id} className="dark-mode">
                              {item.label}
                            </Select.Item>
                          )}
                        </Select>
                        <Text className="text-xs text-destructive">{form.formState.errors.kind?.message}</Text>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                  <Button color="secondary" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button color="primary-destructive" type="submit" isDisabled={isPending}>
                    {isPending ? "Triggering..." : "Trigger Error"}
                  </Button>
                </div>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
